/**
 * Utility functions for the Data Migration tool's remote API operations.
 */

import { postJson, postRun } from './remoteError';

const LIST_MIGRATABLE_WORKOUTS_PATH = 'dataMigration-listMigratableWorkouts';
const MIGRATE_WORKOUTS_PATH = 'dataMigration-migrateWorkoutsToCoach';

/**
 * Reasons a workout cannot be migrated. Mirrors MigrationBlockReason in
 * functions-22/src/dataMigration/utils/compliance.ts — the endpoint returns these
 * verbatim so the UI can render a reason per blocked workout.
 */
export const MigrationBlockReason = {
  CUSTOM_EXERCISE: 'CUSTOM_EXERCISE',
  EDITED_EXERCISE: 'EDITED_EXERCISE',
  MISSING_EXERCISE: 'MISSING_EXERCISE',
  NON_BASE_EXERCISE: 'NON_BASE_EXERCISE',
};

/**
 * Library scope rules a workout can fail on at migration time. Mirrors
 * MigrationScopeRejection in functions-22/src/dataMigration/utils/scope.ts. The list
 * endpoint filters all of these out, so one only appears when a workout changed between
 * being listed and being migrated.
 */
export const MigrationScopeRejection = {
  NOT_FOUND: 'NOT_FOUND',
  WRONG_COACH: 'WRONG_COACH',
  ARCHIVED: 'ARCHIVED',
  NOT_PRIVATE: 'NOT_PRIVATE',
  AI_GENERATED: 'AI_GENERATED',
};

export const WRITE_FAILED = 'WRITE_FAILED';

/** Compliance could not be established for the workout, so it was not copied. */
export const MISSING_ELIGIBILITY = 'MISSING_ELIGIBILITY';

/** The target coach already holds a copy of this workout, from an earlier run. */
export const ALREADY_MIGRATED = 'ALREADY_MIGRATED';

/**
 * Every reason the endpoint can report against a single workout — the same union as
 * MigrationFailureReason in functions-22/src/dataMigration/utils/migrate.ts.
 */
export const MigrationFailureReason = {
  ...MigrationScopeRejection,
  ...MigrationBlockReason,
  WRITE_FAILED,
  MISSING_ELIGIBILITY,
  ALREADY_MIGRATED,
};

/**
 * Fetches the source coach's migratable workout library.
 *
 * Eligibility and ordering are decided server-side — compliant workouts first, blocked
 * after — so the list is handed back in the order received. Sorting it here would break
 * that contract. Archived, PUBLIC and AI-generated workouts are excluded by the endpoint
 * and never appear.
 *
 * @param {string} sourceCoachId - The coach whose library is being migrated from.
 * @param {firebase.remote.RemoteType} remote - The Firebase remote object.
 * @returns {Promise<Object>} { workouts, totalCount, migratableCount, blockedCount }
 */
export const listMigratableWorkouts = async (sourceCoachId, remote) => {
  const result = await postJson(
    remote,
    LIST_MIGRATABLE_WORKOUTS_PATH,
    { sourceCoachId },
    'Failed to load the source coach\'s workout library',
  );

  return {
    workouts: result?.workouts || [],
    totalCount: result?.totalCount || 0,
    migratableCount: result?.migratableCount || 0,
    blockedCount: result?.blockedCount || 0,
  };
};

/**
 * Starts a migration run and waits for the endpoint to finish it.
 *
 * One request for the whole selection: the server owns the run and records it on
 * migrationRun/{runId}, which is where progress comes from while this is in flight.
 * The id is generated by the caller (MigrationRun.reserveRunId) precisely so the page can
 * subscribe before this resolves.
 *
 * Rejects on anything that is not a 200 — a rejection is a failure of the request, not of
 * an individual workout, which the endpoint reports per workout inside a successful
 * response. A rejection no longer means the run did not happen: the request can die while
 * the endpoint keeps going, and the run document is what settles that. `isRunRejected` on
 * the thrown error separates the two, so the caller knows whether there is a run to follow.
 *
 * @param {Object} params - The run.
 * @param {string} params.sourceCoachId - The coach being migrated from.
 * @param {string} params.targetCoachId - The coach being migrated to.
 * @param {string[]} params.workoutIds - The selected workouts, in listed order.
 * @param {string} params.runId - The reserved run document id.
 * @param {firebase.remote.RemoteType} params.remote - The Firebase remote object.
 * @returns {Promise<Object>} { runId, results, migratedCount, failedCount }
 */
export const runMigration = async ({
  sourceCoachId,
  targetCoachId,
  workoutIds,
  runId,
  remote,
}) => {
  /*
    Everything the endpoint turns a run away for — the token, the schema, an unknown target coach
    — is checked before the run document is created and answers 4xx, so a 4xx means no run
    exists to follow; a 5xx may be part-way through a run only the document can settle, unless it
    reports `runCreated: false`. postRun applies that rule as `isRunRejected`.
  */
  const result = await postRun(
    remote,
    MIGRATE_WORKOUTS_PATH,
    {
      sourceCoachId, targetCoachId, workoutIds, runId,
    },
    'Failed to migrate the selected workouts',
  );
  const results = result?.results || [];
  const migratedCount = results.filter(({ success }) => success).length;

  return {
    runId,
    results,
    migratedCount,
    failedCount: results.length - migratedCount,
  };
};

/**
 * Resolves a finished run against the list it was started from, so the summary can name
 * workouts rather than list ids. An id with no matching row falls back to the id itself —
 * that is a workout the list no longer holds, which is worth showing rather than hiding.
 *
 * A migrated workout is named by what it landed as, not by what it was selected as. The
 * two differ whenever the target already held the name: the copy takes a "(copy)" suffix
 * counted up against that library, and an operator told the source name goes looking for
 * a workout that is not there. `newWorkoutName` is the endpoint's account of the name it
 * wrote; the source name stands in only for a server that has not been deployed with it.
 *
 * Two outcomes, not four: the endpoint answers for every workout in the run it completed,
 * so there is no chunk left in an unknown state and nothing that went unsent. A run whose
 * request died is not summarized here at all — the run document is what says how it ended.
 *
 * @param {Object} runResult - The value returned by runMigration.
 * @param {Array} workouts - The workout rows the run was started from.
 * @returns {Object} { migrated, failed }
 */
export const summarizeMigrationRun = (runResult, workouts) => {
  const nameById = new Map(workouts.map(({ id, name }) => [id, name]));
  const named = (id) => ({ id, name: nameById.get(id) || id });

  return {
    migrated: runResult.results
      .filter(({ success }) => success)
      .map(({ sourceWorkoutId, newWorkoutName, suffixApplied }) => ({
        ...named(sourceWorkoutId),
        name: newWorkoutName || nameById.get(sourceWorkoutId) || sourceWorkoutId,
        suffixApplied: !!suffixApplied,
      })),
    failed: runResult.results
      .filter(({ success }) => !success)
      .map(({ sourceWorkoutId, failureReason }) => ({ ...named(sourceWorkoutId), reason: failureReason })),
  };
};

/**
 * Resolves a finished run from its own document, for the case `summarizeMigrationRun`
 * cannot answer: the request died and the document is the only account of the run.
 *
 * The document records what failed and counts what did not — see MigrationRun in
 * functions-22/src/db/types/migration-run.ts, which keeps it that way so a whole run's
 * failures still fit. So the failures are named and the successes are a number, where a
 * run whose request came back names both.
 *
 * @param {MigrationRun} run - The observed run document, at a terminal status.
 * @param {Array} workouts - The workout rows the run was started from.
 * @returns {Object} { migrated, migratedCount, failed }
 */
export const summarizeMigrationRunDocument = (run, workouts) => {
  const nameById = new Map(workouts.map(({ id, name }) => [id, name]));

  return {
    migrated: [],
    migratedCount: run.migrated,
    failed: run.failures.map(({ sourceWorkoutId, reason }) => ({
      id: sourceWorkoutId,
      name: nameById.get(sourceWorkoutId) || sourceWorkoutId,
      reason,
    })),
  };
};
